import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Fab,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import {
  useObcMasterTable,
  ObcMasterTab,
  ObcAccountRow,
  ObcDepartmentRow,
  ObcSubAccountRow,
} from '../hooks/useObcMasterTable';
import { ep } from '../api/endpoints';

const SYNC_API_URL = ep('run_obc_sync');

const DEBIT_CREDIT_LABEL: Record<number, string> = {
  0: '借方',
  1: '貸方',
};

const formatSyncedAt = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ja-JP');
};

const ACCOUNT_COLUMNS: Column[] = [
  {
    field: 'account_code',
    headerName: '勘定科目',
    width: 280,
    renderCell: (row: ObcAccountRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
        >
          {row.account_code}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.account_name}
        </Typography>
      </Box>
    ),
  },
  { field: 'account_kana', headerName: 'フリガナ', width: 160 },
  {
    field: 'category_l3_name',
    headerName: '科目区分 (L3)',
    width: 200,
    renderCell: (row: ObcAccountRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
          {row.category_l3_code || '—'}
        </Typography>
        <Typography variant="body2">{row.category_l3_name || '—'}</Typography>
      </Box>
    ),
  },
  {
    field: 'category_l1_name',
    headerName: '階層 (L1/L2)',
    width: 220,
    renderCell: (row: ObcAccountRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25, fontSize: '0.8rem' }}>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.category_l1_name || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.category_l2_name || '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'debit_credit_type',
    headerName: '貸借',
    width: 72,
    renderCell: (row: ObcAccountRow) => {
      const label =
        row.debit_credit_type != null
          ? DEBIT_CREDIT_LABEL[row.debit_credit_type] ?? String(row.debit_credit_type)
          : '—';
      return label;
    },
  },
  {
    field: 'is_active',
    headerName: '状態',
    width: 88,
    renderCell: (row: ObcAccountRow) => (
      <Chip
        label={row.is_active ? '有効' : '無効'}
        size="small"
        color={row.is_active ? 'success' : 'default'}
        variant="outlined"
        sx={{ height: 22, fontSize: '0.7rem' }}
      />
    ),
  },
  {
    field: 'synced_at',
    headerName: '最終同期',
    width: 150,
    valueFormatter: (v) => formatSyncedAt(v),
  },
];

const DEPARTMENT_COLUMNS: Column[] = [
  {
    field: 'section_code',
    headerName: '部門',
    width: 320,
    renderCell: (row: ObcDepartmentRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
        >
          {row.section_code}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.section_name}
        </Typography>
      </Box>
    ),
  },
  { field: 'section_kana', headerName: 'フリガナ', width: 200 },
  {
    field: 'synced_at',
    headerName: '最終同期',
    width: 150,
    valueFormatter: (v) => formatSyncedAt(v),
  },
];

const SUB_ACCOUNT_COLUMNS: Column[] = [
  {
    field: 'account_code',
    headerName: '勘定科目',
    width: 280,
    renderCell: (row: ObcSubAccountRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
        >
          {row.account_code}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.account_name || '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'sub_account_code',
    headerName: '補助科目',
    width: 320,
    renderCell: (row: ObcSubAccountRow) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
        >
          {row.sub_account_code}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.sub_account_name || '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'synced_at',
    headerName: '最終同期',
    width: 150,
    valueFormatter: (v) => formatSyncedAt(v),
  },
];

export default function ObcMasterManagement() {
  const [tab, setTab] = useState<ObcMasterTab>('account');
  const [inputWord, setInputWord] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    data,
    totalCount,
    lastSyncedAt,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    applySearch,
    refresh,
  } = useObcMasterTable(tab);

  const handleTabChange = (_: React.SyntheticEvent, value: ObcMasterTab) => {
    setTab(value);
    setInputWord('');
    setPage(0);
    applySearch('');
  };

  const handleSearch = useCallback(() => {
    applySearch(inputWord.trim());
  }, [applySearch, inputWord]);

  const handleClearSearch = useCallback(() => {
    setInputWord('');
    applySearch('');
  }, [applySearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleMasterSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setSyncError(null);
    try {
      const response = await axios.post(SYNC_API_URL, { type: 'master' });
      if (response.data.status === 'success') {
        const counts = response.data.counts || {};
        setSyncMessage(
          `マスタ同期を完了しました（部門: ${counts.departments ?? '—'}件、勘定科目: ${counts.accounts ?? '—'}件）`
        );
        refresh();
      } else {
        setSyncError(response.data.message || '同期に失敗しました');
      }
    } catch (err: any) {
      setSyncError(err.response?.data?.message || err.message || '同期中にエラーが発生しました');
    } finally {
      setSyncing(false);
    }
  };

  const columns =
    tab === 'account'
      ? ACCOUNT_COLUMNS
      : tab === 'department'
      ? DEPARTMENT_COLUMNS
      : SUB_ACCOUNT_COLUMNS;
  const searchPlaceholder =
    tab === 'account'
      ? '科目コード / 科目名 / フリガナ / 科目区分'
      : tab === 'department'
      ? '部門コード / 部門名 / フリガナ'
      : '勘定科目コード / 科目名 / 補助科目コード / 補助科目名';

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', px: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}
          >
            <Icons.AccountBalance /> OBC マスタ管理
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            勘定奉行から同期したマスタを参照します（編集は OBC 側で行い、同期で反映）
          </Typography>
          {lastSyncedAt && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: blueGrey[400] }}>
              テーブル最終同期: {formatSyncedAt(lastSyncedAt)}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={inputWord}
            onChange={(e) => setInputWord(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              minWidth: 350,
              '& .MuiInputBase-root': { fontSize: '0.8rem' },
              '& .MuiInputBase-input': { py: 0.5 },
            }}
            InputProps={{
              endAdornment: inputWord ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} aria-label="検索条件をクリア">
                    <Icons.Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Fab
            variant="extended"
            onClick={handleMasterSync}
            sx={STANDARD_FAB_CTA_SX}
            disabled={syncing || loading}
          >
            {syncing ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : <Icons.CloudSync />}
            OBC から同期
          </Fab>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{ mt: 2, px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="account" label={`勘定科目 (${tab === 'account' ? totalCount : '—'})`} />
        <Tab value="sub_account" label={`補助科目 (${tab === 'sub_account' ? totalCount : '—'})`} />
        <Tab value="department" label={`部門 (${tab === 'department' ? totalCount : '—'})`} />
      </Tabs>

      {syncMessage && (
        <Alert severity="success" sx={{ mt: 1.5, mx: 2 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}
      {syncError && (
        <Alert severity="error" sx={{ mt: 1.5, mx: 2 }} onClose={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5, mx: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ ...TABLE_FILL_SX, mt: 1 }}>
        <StandardDataTable
          rows={data}
          columns={columns}
          loading={loading}
          loadingMessage={
            tab === 'account'
              ? '勘定科目を取得中'
              : tab === 'department'
              ? '部門を取得中'
              : '補助科目を取得中'
          }
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          onSortChange={() => {}}
        />
      </Box>
    </Box>
  );
}
